import React from 'react';
import { bool, func, node, object } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';

import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';
import { createSignupPasswordValidator } from '../../../util/signupValidation';
import { Form, PrimaryButton, FieldTextInput } from '../../../components';
import SignupSecurity from '../SignupSecurity/SignupSecurity';

import css from './SignupHostForm.module.css';

const SignupHostFormComponent = props => {
  const { intl, onSubmit, signupError } = props;
  const validateSignupPasswords = createSignupPasswordValidator(intl);
  const submitWithPasswordGuard = values => {
    const passwordErrors = validateSignupPasswords(values);

    return Object.keys(passwordErrors).length > 0 ? passwordErrors : onSubmit(values);
  };

  return (
    <FinalForm
      {...props}
      mutators={{ ...arrayMutators }}
      validate={validateSignupPasswords}
      onSubmit={submitWithPasswordGuard}
      render={fieldRenderProps => {
        const {
          rootClassName,
          className,
          formId,
          handleSubmit,
          inProgress,
          invalid,
          intl,
          termsAndConditions,
          values,
          form,
        } = fieldRenderProps;

        // email
        const emailRequired = validators.required(
          intl.formatMessage({
            id: 'SignupForm.emailRequired',
          })
        );
        const emailValid = validators.emailFormatValid(
          intl.formatMessage({
            id: 'SignupForm.emailInvalid',
          })
        );

        const classes = classNames(rootClassName || css.root, className);
        const submitInProgress = inProgress;
        const submitDisabled = invalid || submitInProgress || !values.turnstileToken;
        return (
          <Form className={classes} onSubmit={handleSubmit}>
            <div className={css.formRow}>
              <div className={css.formFld}>
                <FieldTextInput
                  type="email"
                  id={formId ? `${formId}.email` : 'email'}
                  name="email"
                  autoComplete="email"
                  aria-label={intl.formatMessage({ id: 'SignupForm.emailLabel' })}
                  // label={intl.formatMessage({
                  //   id: 'SignupForm.emailLabel',
                  // })}
                  placeholder={intl.formatMessage({
                    id: 'SignupForm.emailPlaceholder',
                  })}
                  validate={validators.composeValidators(emailRequired, emailValid)}
                />
              </div>
            </div>
            <div className={classNames(css.formRow, css.halfWidth)}>
              <div className={css.formFld}>
                <FieldTextInput
                  className={css.firstNameRoot}
                  type="text"
                  id={formId ? `${formId}.fname` : 'fname'}
                  name="fname"
                  autoComplete="given-name"
                  aria-label={intl.formatMessage({ id: 'SignupForm.firstNameLabel' })}
                  // label={intl.formatMessage({
                  //   id: 'SignupForm.firstNameLabel',
                  // })}
                  placeholder={intl.formatMessage({
                    id: 'SignupForm.firstNamePlaceholder',
                  })}
                  validate={validators.required(
                    intl.formatMessage({
                      id: 'SignupForm.firstNameRequired',
                    })
                  )}
                />
              </div>
              <div className={css.formFld}>
                <FieldTextInput
                  className={css.lastNameRoot}
                  type="text"
                  id={formId ? `${formId}.lname` : 'lname'}
                  name="lname"
                  autoComplete="family-name"
                  aria-label={intl.formatMessage({ id: 'SignupForm.lastNameLabel' })}
                  // label={intl.formatMessage({
                  //   id: 'SignupForm.lastNameLabel',
                  // })}
                  placeholder={intl.formatMessage({
                    id: 'SignupForm.lastNamePlaceholder',
                  })}
                  validate={validators.required(
                    intl.formatMessage({
                      id: 'SignupForm.lastNameRequired',
                    })
                  )}
                />
              </div>
            </div>
            <div className={css.formRow}>
              <div className={css.formFld}>
                <FieldTextInput
                  className={css.password}
                  type="password"
                  id={formId ? `${formId}.password` : 'password'}
                  name="password"
                  autoComplete="new-password"
                  aria-label={intl.formatMessage({ id: 'SignupForm.passwordLabel' })}
                  // label={intl.formatMessage({
                  //   id: 'SignupForm.passwordLabel',
                  // })}
                  placeholder={intl.formatMessage({
                    id: 'SignupForm.passwordPlaceholder',
                  })}
                />
              </div>
            </div>
            <div className={css.formRow}>
              <div className={css.formFld}>
                <FieldTextInput
                  className={css.password}
                  type="password"
                  id={formId ? `${formId}.confirmPassword` : 'confirmPassword'}
                  name="confirmPassword"
                  autoComplete="new-password"
                  aria-label={intl.formatMessage({ id: 'SignupForm.confirmPasswordLabel' })}
                  // label={intl.formatMessage({
                  //   id: 'SignupForm.passwordLabel',
                  // })}
                  placeholder={intl.formatMessage({
                    id: 'SignupForm.confirmPasswordPlaceholder',
                  })}
                />
              </div>
            </div>

            <SignupSecurity form={form} signupError={signupError} />

            <div className={css.formRow}>
              <div className={css.formFld}>
                {termsAndConditions}
                <PrimaryButton
                  type="submit"
                  inProgress={submitInProgress}
                  disabled={submitDisabled}
                >
                  <FormattedMessage id="SignupForm.signUp" />
                </PrimaryButton>
              </div>
            </div>
          </Form>
        );
      }}
    />
  );
};

SignupHostFormComponent.defaultProps = { inProgress: false, signupError: null };

SignupHostFormComponent.propTypes = {
  inProgress: bool,
  onSubmit: func.isRequired,
  signupError: object,
  termsAndConditions: node.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const SignupHostForm = compose(injectIntl)(SignupHostFormComponent);
SignupHostForm.displayName = 'SignupHostForm';

export default SignupHostForm;
